import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatChatTimestamp } from '../../data_system/data_functions/chatting_date_function/chatting_date_function';
import { deleteChattingMessageRequest, getChattingDetailRequest } from '../../api/main/chatting/ChattingRequest';

const PAGE_SIZE = 50;

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const ServerChatDetailContent = ({ chatId, readOnly }) => {
    const requestSequence = useRef(0);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [reloadKey, setReloadKey] = useState(0);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        const sequence = ++requestSequence.current;
        setLoading(true);
        setError('');
        getChattingDetailRequest(chatId, { page, size: PAGE_SIZE, search })
            .then((response) => {
                if (sequence !== requestSequence.current) return;
                setDetail(response.data.data);
                setLoading(false);
            })
            .catch((requestError) => {
                if (sequence !== requestSequence.current) return;
                setError(errorMessage(requestError, '대화 내용을 불러오지 못했습니다.'));
                setLoading(false);
            });
        return () => {
            if (sequence === requestSequence.current) requestSequence.current += 1;
        };
    }, [chatId, page, search, reloadKey]);

    const copyMessage = async (message) => {
        setNotice('');
        try {
            await navigator.clipboard.writeText(message.content);
            setNotice('메시지를 복사했습니다.');
        } catch (_) {
            setNotice('메시지를 복사하지 못했습니다.');
        }
    };

    const deleteMessage = async (message) => {
        if (deletingId || !window.confirm('해당 메시지를 삭제하시겠습니까?')) return;
        setDeletingId(message.id);
        setNotice('');
        try {
            await deleteChattingMessageRequest(chatId, message.id);
            setNotice('메시지를 삭제했습니다.');
            setReloadKey((key) => key + 1);
        } catch (requestError) {
            setNotice(errorMessage(requestError, '메시지를 삭제하지 못했습니다.'));
        } finally {
            setDeletingId(null);
        }
    };

    const messages = detail?.messages ? [...detail.messages].reverse() : [];
    const totalPages = Math.max(1, Math.ceil((detail?.total || 0) / PAGE_SIZE));

    return (
        <Wrapper>
            <SearchForm
                onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setSearch(searchInput.trim());
                }}
            >
                <SearchInput aria-label="메시지 검색" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="메시지 검색" />
                <button type="submit">검색</button>
                {search && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearchInput('');
                            setSearch('');
                            setPage(1);
                        }}
                    >
                        초기화
                    </button>
                )}
            </SearchForm>

            {notice && <Status role="status">{notice}</Status>}
            {loading && <Status>대화 내용을 불러오는 중입니다.</Status>}
            {!loading && error && (
                <Status role="alert">
                    {error} <button onClick={() => setReloadKey((key) => key + 1)}>다시 시도</button>
                </Status>
            )}
            {!loading && !error && messages.length === 0 && <Status>표시할 메시지가 없습니다.</Status>}
            {!loading &&
                !error &&
                messages.map((message) => (
                    <Message key={message.id} $deleted={message.delete_yn}>
                        <MessageHeader>
                            <span>{message.admin_yn ? '관리자' : `사용자 ${message.user_id}`}</span>
                            <time>{formatChatTimestamp(message.created_at)}</time>
                        </MessageHeader>
                        {message.chatting_type === 1 && !message.delete_yn ? <MessageImage src={message.content} alt="채팅 첨부 이미지" /> : <Content>{message.content}</Content>}
                        {!message.delete_yn && (
                            <Actions>
                                <button type="button" onClick={() => copyMessage(message)}>
                                    복사
                                </button>
                                {!readOnly && message.can_delete && (
                                    <button type="button" disabled={deletingId === message.id} onClick={() => deleteMessage(message)}>
                                        {deletingId === message.id ? '삭제 중' : '삭제'}
                                    </button>
                                )}
                            </Actions>
                        )}
                    </Message>
                ))}
            {!loading && !error && detail?.total > PAGE_SIZE && (
                <Pagination aria-label="메시지 페이지">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                        이전
                    </button>
                    <span>
                        {page} / {totalPages}
                    </span>
                    <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
                        다음
                    </button>
                </Pagination>
            )}
        </Wrapper>
    );
};

const ServerChatDetail = ({ chatId, readOnly = false }) => <ServerChatDetailContent key={chatId} chatId={chatId} readOnly={readOnly} />;

export default ServerChatDetail;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchForm = styled.form`
    display: flex;
    gap: 6px;

    button,
    input {
        border: 1px solid #aab4c0;
        border-radius: 5px;
        padding: 7px;
    }
`;

const SearchInput = styled.input`
    flex: 1;
`;

const Status = styled.div`
    padding: 12px;
    text-align: center;
`;

const Message = styled.article`
    align-self: ${(props) => (props.$deleted ? 'center' : 'stretch')};
    padding: 10px;
    border-radius: 7px;
    background: ${(props) => (props.$deleted ? '#ececec' : '#fff')};
`;

const MessageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
    color: #555;
    font-size: 12px;
`;

const Content = styled.div`
    overflow-wrap: anywhere;
    white-space: pre-wrap;
`;

const MessageImage = styled.img`
    display: block;
    max-width: 260px;
    max-height: 260px;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
`;

const Pagination = styled.nav`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
`;
